import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import { METADATA_TYPES } from '../../../common/constants'
import { buildTreeHierarchy, transformTree } from '../../../common/helpers'

import {
  ADD_DISH_TYPE,
  DELETE_DISH_TYPE,
  EXPAND_DISH_TYPE_MAIN_TREE_KEYS,
  EXPAND_DISH_TYPE_SUPPORTING_TREE_KEYS,
  FETCH_DISH_TYPES,
  MOVE_DISH_TYPES,
  RENAME_DISH_TYPE,
  REPLACE_DISH_TYPE,
  RESET_DISH_TYPE_TREE,
  REVERT_REPLACE_DISH_TYPE,
  SAVE_DISH_TYPES,
  SET_DATA_FOR_RENAMING_DISH_TYPE,
  SET_IS_CURATION_ENABLED_DISH_TYPE,
} from '../action-types'
import { DISH_TYPES_INITIAL_STATE } from '../initial-states'

const dishTypesReducer = (state = DISH_TYPES_INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_DISH_TYPES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_DISH_TYPES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.dishTypes
        const treeData = buildTreeHierarchy(results, METADATA_TYPES.DISH_TYPES)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_DISH_TYPES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_DISH_TYPES.CLEANUP:
      return { ...DISH_TYPES_INITIAL_STATE }

    case SAVE_DISH_TYPES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_DISH_TYPES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.saveDishTypes

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.DISH_TYPES)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_DISH_TYPES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          latestEvent: undefined,
        }),
      )

    case DELETE_DISH_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_DISH_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.deleteDishType

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.DISH_TYPES)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_DISH_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REPLACE_DISH_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REPLACE_DISH_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.replaceDishType

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.DISH_TYPES)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REPLACE_DISH_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REVERT_REPLACE_DISH_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REVERT_REPLACE_DISH_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.revertReplaceDishType

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.DISH_TYPES)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REVERT_REPLACE_DISH_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_DISH_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_DISH_TYPE.COMPLETE:
      return produce(state, nextState => {
        const updatedDishType = action.payload.data.setIsCurationEnabledDishType

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedDishType.id
            ? {
                ...node,
                isCurationEnabled: updatedDishType.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_DISH_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_DISH_TYPE:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id },
        })
      })

    case RENAME_DISH_TYPE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_DISH_TYPE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_DISH_TYPES:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = [
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ]
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: buildTreeHierarchy(newTreeData, METADATA_TYPES.DISH_TYPES),
        })
      })

    case EXPAND_DISH_TYPE_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_DISH_TYPE_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_DISH_TYPE_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    default:
      return state
  }
}

export default dishTypesReducer
